import { ArrowLeft, CircleAlert, Search, Undo2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { usePageHeader } from "../../components/shell/PageHeaderContext";
import { Spinner } from "../../components/Spinner";
import { apiRequest } from "../../lib/api";
import { formatTimestamp } from "../../lib/format";
import { useAdminApartado, useAdminApartados, useInvalidateAll } from "../../lib/queries";
import { APARTADO_STATUS_LABELS, REQUEST_STATUS_LABELS, movementCopy, mxn } from "../../lib/reservations";
import type { ApartadoMovement, ApartadoStatus } from "../../types";
import { AuditList, ConfirmButton, Feedback, Field, KeyValues, Pill, StatTile, useAction } from "./shared";

const TONE: Record<ApartadoStatus, "ok" | "warn" | "bad" | "muted"> = { active: "ok", funding_pending: "warn", funding_failed: "bad", completed: "muted", closed: "muted" };
const DESTINATIONS: Array<{ id: "trip_payment" | "service_payment" | "refund_to_wallet" | "other"; label: string; hint: string }> = [
  { id: "trip_payment", label: "Aplicar al pago del viaje", hint: "Anticipo o liquidación con el operador. Cuenta como aplicado." },
  { id: "service_payment", label: "Pago de un servicio autorizado", hint: "Vuelo, seguro, noche extra… Cuenta como aplicado." },
  { id: "refund_to_wallet", label: "Devolver a Wallet Libre", hint: "Libera el dinero en la misma cuenta del cliente (servicios no confirmados, cancelación)." },
  { id: "other", label: "Otro destino aprobado", hint: "Solo con referencia de aprobación." }
];

/** Every Apartado with its earmark, applied amount and owner; totals by status on top. */
export function AdminApartadosPage() {
  usePageHeader("Apartados", "Fondos destinados a viajes: metas, aportaciones, aplicación de fondos y devoluciones.");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const data = useAdminApartados({ q, status });
  const totals = data.data?.totals ?? {};
  const active = totals.active ?? { count: 0, balance: 0 };
  return (
    <div className="page-container admin-stack">
      <div className="stat-grid">
        <StatTile label="Apartados activos" value={String(active.count)} hint="con fondos destinados a un viaje" compact />
        <StatTile label="Total apartado" value={mxn(active.balance)} hint="suma de los saldos activos" compact />
        <StatTile label="Completados" value={String(totals.completed?.count ?? 0)} hint="fondos aplicados al viaje" compact />
        <StatTile label="Cerrados" value={String(totals.closed?.count ?? 0)} compact />
      </div>
      <div className="discover-toolbar">
        <label className="search-field"><Search size={18} aria-hidden="true" /><input type="search" value={q} onChange={(event) => setQ(event.target.value)} placeholder="Viaje, cliente, correo o folio" aria-label="Buscar Apartados" /></label>
        <select className="standalone-input small" value={status} onChange={(event) => setStatus(event.target.value)} aria-label="Estado" style={{ width: 200 }}>
          <option value="">Todos</option>
          {(Object.keys(APARTADO_STATUS_LABELS) as ApartadoStatus[]).map((value) => <option key={value} value={value}>{APARTADO_STATUS_LABELS[value]}</option>)}
        </select>
      </div>
      {data.isLoading ? <div className="page-state"><Spinner label="Cargando Apartados" /></div> : (
        <div className="table-wrap card" style={{ padding: 0 }}>
          <table className="admin-table">
            <thead><tr><th>Cliente</th><th>Viaje</th><th>Solicitud</th><th>Saldo</th><th>Aplicado</th><th>Meta</th><th>Estado</th><th>Actualizado</th><th /></tr></thead>
            <tbody>
              {(data.data?.apartados ?? []).length === 0 && <tr><td colSpan={9} className="table-empty">Sin Apartados con esos filtros.</td></tr>}
              {(data.data?.apartados ?? []).map((apartado) => (
                <tr key={apartado.id}>
                  <td>{apartado.customer.fullName}<br /><span className="request-meta">{apartado.customer.email}</span></td>
                  <td>{apartado.tripName}</td>
                  <td translate="no">{apartado.folio}<br /><span className="request-meta">{REQUEST_STATUS_LABELS[apartado.requestStatus]}</span></td>
                  <td translate="no"><strong>{mxn(apartado.balance)}</strong></td>
                  <td translate="no">{mxn(apartado.appliedAmount)}</td>
                  <td translate="no">{mxn(apartado.goalAmount)} · {Math.round((apartado.progress ?? 0) * 100)}%</td>
                  <td><Pill label={APARTADO_STATUS_LABELS[apartado.status]} tone={TONE[apartado.status]} /></td>
                  <td translate="no">{formatTimestamp(apartado.updatedAt)}</td>
                  <td><Link className="secondary-button small" to={`/admin/apartados/${apartado.id}`}>Abrir</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** One Apartado: goal changes both ways within the floor, fund disposal with purpose and approval evidence, reversals, full history (C5.2/C5.3). */
export function AdminApartadoPage() {
  const { id } = useParams();
  const detail = useAdminApartado(id);
  const invalidate = useInvalidateAll();
  const action = useAction();
  const apartado = detail.data?.apartado;
  usePageHeader(apartado ? `Apartado · ${apartado.tripName}` : "Apartado", apartado ? `${apartado.customer.fullName} · solicitud ${apartado.folio}` : undefined);
  const [goal, setGoal] = useState("");
  const [goalReason, setGoalReason] = useState("");
  const [goalReference, setGoalReference] = useState("");
  const [goalApproval, setGoalApproval] = useState("");
  const [amount, setAmount] = useState("");
  const [destination, setDestination] = useState<typeof DESTINATIONS[number]["id"]>("trip_payment");
  const [reason, setReason] = useState("");
  const [reference, setReference] = useState("");
  const [approval, setApproval] = useState("");
  const [closeReason, setCloseReason] = useState("");

  if (detail.isLoading) return <div className="page-state"><Spinner label="Cargando Apartado" /></div>;
  if (detail.isError || !apartado) return <div className="page-state error"><CircleAlert aria-hidden="true" /><strong>No encontramos este Apartado</strong><Link className="secondary-button" to="/admin/apartados">Volver</Link></div>;
  const proposedGoal = Number(goal.replace(/[^\d.]/g, "")) || 0;
  const goalDiff = proposedGoal ? proposedGoal - apartado.goalAmount : 0;

  const changeGoal = async (event: FormEvent) => {
    event.preventDefault();
    await action.run("goal", async () => {
      await apiRequest(`/api/v1/admin/apartados/${apartado.id}/goal`, { method: "PATCH", body: JSON.stringify({ goalAmount: proposedGoal, reason: goalReason, reference: goalReference || undefined, approvalReference: goalApproval || undefined }) });
      await invalidate();
      setGoal(""); setGoalReason(""); setGoalReference(""); setGoalApproval("");
      return "Meta actualizada. El cliente recibe la notificación.";
    });
  };
  const dispose = async (event: FormEvent) => {
    event.preventDefault();
    await action.run("dispose", async () => {
      await apiRequest(`/api/v1/admin/apartados/${apartado.id}/disposals`, {
        method: "POST", headers: { "idempotency-key": crypto.randomUUID() },
        body: JSON.stringify({ amount: Number(amount.replace(/[^\d.]/g, "")), destination, reason, reference, approvalReference: approval || undefined })
      });
      await invalidate();
      setAmount(""); setReason(""); setReference(""); setApproval("");
      return "Disposición registrada: el saldo del Apartado y el historial ya la reflejan.";
    });
  };
  const reverse = async (movement: ApartadoMovement) => {
    const why = window.prompt("Motivo de la reversión (queda en el historial):");
    if (!why || why.trim().length < 3) return;
    await action.run("reverse", async () => {
      await apiRequest(`/api/v1/admin/apartados/${apartado.id}/movements/${movement.id}/reverse`, { method: "POST", body: JSON.stringify({ reason: why.trim() }) });
      await invalidate();
      return "Movimiento revertido con un asiento vinculado.";
    });
  };
  const close = async (statusValue: "completed" | "closed") => {
    await apiRequest(`/api/v1/admin/apartados/${apartado.id}/close`, { method: "POST", body: JSON.stringify({ reason: closeReason || (statusValue === "completed" ? "Fondos aplicados al viaje" : "Cierre operativo"), status: statusValue }) });
    await invalidate();
  };

  return (
    <div className="page-container admin-stack">
      <Link className="back-button" to="/admin/apartados"><ArrowLeft size={16} aria-hidden="true" /> Apartados</Link>
      <div className="stat-grid">
        <StatTile label="Saldo apartado" value={mxn(apartado.balance)} hint="destinado al viaje" compact />
        <StatTile label="Aplicado al viaje" value={mxn(apartado.appliedAmount)} compact />
        <StatTile label="Meta" value={mxn(apartado.goalAmount)} hint={`${Math.round((apartado.progress ?? 0) * 100)}% · mínimo permitido ${mxn(apartado.minimumGoal)}`} compact />
        <StatTile label="Wallet Libre (Kuspit)" value={detail.data?.wallet.available !== null && detail.data?.wallet.available !== undefined ? mxn(detail.data.wallet.available) : "—"} hint={`${mxn(detail.data?.wallet.reserved ?? 0)} reservados en Apartados`} compact />
      </div>
      <div className="admin-two-col">
        <div className="admin-stack">
          <section className="card">
            <div className="section-heading" style={{ margin: 0 }}>
              <div><span className="page-eyebrow">Apartado</span><h2>{apartado.tripName}</h2></div>
              <Pill label={APARTADO_STATUS_LABELS[apartado.status]} tone={TONE[apartado.status]} />
            </div>
            <KeyValues rows={[
              { label: "Cliente", value: <Link to={`/admin/usuarios/${apartado.customer.id}`}>{apartado.customer.fullName} · {apartado.customer.email}</Link> },
              { label: "Solicitud", value: <Link to={`/admin/solicitudes/${apartado.requestId}`}>{apartado.folio} · {REQUEST_STATUS_LABELS[apartado.requestStatus]}</Link> },
              { label: "Creado", value: formatTimestamp(apartado.createdAt) },
              { label: "Consentimiento", value: `${apartado.consentVersion}${apartado.documentsVersion ? ` · ${apartado.documentsVersion}` : ""}`, mono: true },
              { label: "Base comprometida", value: mxn(apartado.baseAmount) }
            ]} />
          </section>
          <section className="card">
            <span className="rail-title">Historial de movimientos (auditoría completa)</span>
            <div className="table-wrap">
              <table className="admin-table" style={{ minWidth: 700 }}>
                <thead><tr><th>Fecha</th><th>Movimiento</th><th>Actor</th><th>Monto</th><th>Saldo</th><th>Referencia</th><th>Estado</th><th /></tr></thead>
                <tbody>
                  {apartado.movements.map((movement) => {
                    const copy = movementCopy(movement);
                    return (
                      <tr key={movement.id} className={movement.status === "reversed" ? "muted" : undefined}>
                        <td translate="no">{formatTimestamp(movement.createdAt)}</td>
                        <td><strong>{copy.title}</strong><br /><span className="request-meta">{movement.reason ?? copy.sub}{movement.kind === "goal_change" ? ` · ${mxn(movement.previousGoal ?? 0)} → ${mxn(movement.newGoal ?? 0)}` : ""}</span></td>
                        <td>{movement.actorType}</td>
                        <td translate="no">{movement.kind === "goal_change" ? "—" : `${movement.amount > 0 ? "+" : ""}${mxn(movement.amount)}`}</td>
                        <td translate="no">{mxn(movement.balanceAfter)}</td>
                        <td translate="no">{[movement.reference, movement.approvalReference ? `apr. ${movement.approvalReference}` : null].filter(Boolean).join(" · ") || "—"}<br /><span className="request-meta">{movement.correlationId?.slice(0, 8)}</span></td>
                        <td><Pill label={movement.status === "completed" ? "Confirmado" : movement.status === "reversed" ? "Revertido" : movement.status === "pending" ? "En proceso" : "Fallido"} tone={movement.status === "completed" ? "ok" : movement.status === "reversed" ? "muted" : movement.status === "pending" ? "warn" : "bad"} /></td>
                        <td>{movement.kind === "disposal" && movement.status === "completed" && apartado.status !== "closed" && <button type="button" className="link-button" onClick={() => void reverse(movement)}><Undo2 size={13} aria-hidden="true" /> Revertir</button>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
          <section className="card"><span className="rail-title">Bitácora</span><AuditList events={detail.data?.audit ?? []} /></section>
        </div>

        <div className="admin-stack">
          <Feedback error={action.error} notice={action.notice} />
          <form className="card admin-form" onSubmit={changeGoal}>
            <span className="rail-title">Ajustar meta</span>
            <p className="field-hint">Aumentos por nuevos servicios cotizados o cambios de precio; disminuciones nunca por debajo de lo aportado y aplicado (<span translate="no">{mxn(apartado.minimumGoal)}</span>).</p>
            <Field label="Nueva meta (MXN)" htmlFor="a-goal"><input id="a-goal" className="standalone-input" inputMode="decimal" value={goal} onChange={(event) => setGoal(event.target.value)} placeholder={String(Math.round(apartado.goalAmount))} /></Field>
            {proposedGoal > 0 && (
              <div className="after-grid">
                <div><small>Actual</small><strong translate="no">{mxn(apartado.goalAmount)}</strong></div>
                <div><small>Propuesta</small><strong translate="no">{mxn(proposedGoal)}</strong></div>
                <div><small>Diferencia</small><strong translate="no">{goalDiff > 0 ? "+" : ""}{mxn(goalDiff)}</strong></div>
              </div>
            )}
            <Field label="Motivo operativo" htmlFor="a-goal-reason"><input id="a-goal-reason" className="standalone-input" value={goalReason} onChange={(event) => setGoalReason(event.target.value)} required minLength={3} maxLength={600} placeholder="Nueva cotización del operador, cambio de temporada…" /></Field>
            <div className="field-grid">
              <Field label="Servicio / reserva" htmlFor="a-goal-ref"><input id="a-goal-ref" className="standalone-input" value={goalReference} onChange={(event) => setGoalReference(event.target.value)} maxLength={120} /></Field>
              <Field label="Referencia de aprobación" htmlFor="a-goal-apr"><input id="a-goal-apr" className="standalone-input" value={goalApproval} onChange={(event) => setGoalApproval(event.target.value)} maxLength={120} /></Field>
            </div>
            <p className="field-hint">Consecuencia visible para el cliente: el porcentaje de avance y el faltante cambian; no se mueve dinero.</p>
            <button type="submit" className="secondary-button" disabled={!proposedGoal || proposedGoal === apartado.goalAmount || proposedGoal < apartado.minimumGoal || goalReason.trim().length < 3 || action.busy !== null || apartado.status === "closed"}>{action.busy === "goal" ? <Spinner /> : null} Guardar meta</button>
          </form>

          <form className="card admin-form" onSubmit={dispose}>
            <span className="rail-title">Disponer de fondos</span>
            <Field label="Monto (MXN)" htmlFor="a-amount" hint={`Disponible en el Apartado: ${mxn(apartado.balance)}`}><input id="a-amount" className="standalone-input" inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} /></Field>
            <div className="field-block">
              <span className="field-label">Destino aprobado</span>
              <div className="loan-terms compact" role="radiogroup">
                {DESTINATIONS.map((option) => (
                  <label key={option.id} className={`loan-term${destination === option.id ? " selected" : ""}`}>
                    <input type="radio" name="destination" value={option.id} checked={destination === option.id} onChange={() => setDestination(option.id)} />
                    <strong>{option.label}</strong><span>{option.hint}</span>
                  </label>
                ))}
              </div>
            </div>
            <Field label="Propósito" htmlFor="a-reason"><input id="a-reason" className="standalone-input" value={reason} onChange={(event) => setReason(event.target.value)} required minLength={3} maxLength={600} /></Field>
            <div className="field-grid">
              <Field label="Referencia de servicio / reserva" htmlFor="a-ref"><input id="a-ref" className="standalone-input" value={reference} onChange={(event) => setReference(event.target.value)} required minLength={2} maxLength={120} /></Field>
              <Field label="Evidencia de aprobación" htmlFor="a-apr"><input id="a-apr" className="standalone-input" value={approval} onChange={(event) => setApproval(event.target.value)} maxLength={120} required={destination === "other"} /></Field>
            </div>
            <p className="field-hint">El saldo y el historial del cliente cambian al confirmar. La liquidación al operador se ejecuta por Operaciones contra la referencia capturada.</p>
            <button type="submit" className="primary-button solid" disabled={!Number(amount) || Number(amount) > apartado.balance || reason.trim().length < 3 || reference.trim().length < 2 || action.busy !== null || apartado.status === "closed"}>{action.busy === "dispose" ? <Spinner /> : null} Registrar disposición</button>
          </form>

          <section className="card admin-form">
            <span className="rail-title">Cierre</span>
            <Field label="Motivo" htmlFor="a-close"><input id="a-close" className="standalone-input" value={closeReason} onChange={(event) => setCloseReason(event.target.value)} maxLength={600} /></Field>
            <div className="inline-actions">
              <ConfirmButton label="Marcar como completado" title="Marcar Apartado como completado" message="Indica que los fondos ya se aplicaron al viaje. El Apartado deja de aceptar aportaciones." confirmLabel="Completar" onConfirm={() => close("completed")} disabled={apartado.status !== "active"} />
              <ConfirmButton label="Cerrar Apartado" title="Cerrar Apartado" message="Solo con saldo en cero. El registro y su historial se conservan; la solicitud seguirá mostrando «Ver apartado»." confirmLabel="Cerrar" danger onConfirm={() => close("closed")} disabled={apartado.status === "closed" || apartado.balance > 0} />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
